/**
 * Pure helpers for building the ordered participant list and deriving the
 * effective featured participant in spotlight layout.
 *
 * These functions are deliberately free of React/zustand so they can be
 * unit-tested without rendering anything (T004).
 */

import type { PeerInfo, PeerMediaState } from './store.js'
import { isMirrored, type CameraFacing } from './camera.js'

/** Normalized shape used by both grid and spotlight layouts. */
export interface ParticipantTile {
  id: string
  label: string
  stream: MediaStream
  mediaState: PeerMediaState
  /** True only for the local participant's own tile. */
  isLocal: boolean
  /** True only for the local tile — video is mirrored and audio is muted. */
  mirrored: boolean
  muted: boolean
}

/**
 * Build the canonical ordered participant list: local participant first (when
 * localPeerId is non-null), then remote peers in their insertion order.
 *
 * Each entry is a normalized `ParticipantTile` so VideoTile can render any
 * participant uniformly.
 */
export function orderedParticipants(
  localPeerId: string | null,
  localStream: MediaStream | null,
  localLabel: string,
  localMediaState: PeerMediaState,
  peers: Record<string, PeerInfo>,
  cameraFacing: CameraFacing = 'user',
): ParticipantTile[] {
  const result: ParticipantTile[] = []

  if (localPeerId !== null) {
    result.push({
      id: localPeerId,
      label: localLabel,
      stream: localStream ?? new MediaStream(),
      mediaState: localMediaState,
      isLocal: true,
      mirrored: isMirrored(cameraFacing),
      muted: true,
    })
  }

  for (const peer of Object.values(peers)) {
    result.push({
      id: peer.peerId,
      label: peer.displayName,
      stream: peer.stream,
      mediaState: peer.mediaState,
      isLocal: false,
      mirrored: false,
      muted: false,
    })
  }

  return result
}

/**
 * Derive the id of the participant that should occupy the spotlight featured
 * slot. Implements the default/fallback rule from data-model.md:
 *
 *   1. If pinnedId is set AND found in participants → pinnedId  (FR-005)
 *   2. Else if localPeerId is found in participants → localPeerId  (FR-006 default)
 *   3. Else → first participant id, or null  (empty/edge case)
 *
 * A stale pinnedId (participant left) automatically falls back without the
 * caller having to clear it, satisfying FR-007.
 */
export function effectiveFeaturedId(
  participants: ParticipantTile[],
  pinnedId: string | null,
  localPeerId: string | null,
): string | null {
  if (participants.length === 0) return null

  const ids = new Set(participants.map((p) => p.id))

  if (pinnedId !== null && ids.has(pinnedId)) return pinnedId
  if (localPeerId !== null && ids.has(localPeerId)) return localPeerId
  return participants[0]?.id ?? null
}
